import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, HelpCircle } from "lucide-react";
import PageShell from "@/components/PageShell";
import Seo from "@/components/Seo";

const FAQS = [
  {
    q: "How do I place an order?",
    a: "Choose a box of 10 vials (3 mL or 10 mL) and add it to your cart, then email us to place your order. For bulk and institutional orders, use the wholesale quote form or send an enquiry. Please allow up to 3 business days for confirmation — we confirm availability, pricing, and a shipping quote by email.",
  },
  {
    q: "What payment methods do you accept?",
    a: "Payment is arranged by Zelle or ACH bank transfer. Email us to place your order and we'll reply with payment details and your order total. We never see or store your payment card number.",
  },
  {
    q: "What are the bulk pricing tiers?",
    a: "Prices drop per box as you order more. The bulk tiers are shown on each product and apply automatically at the relevant quantities. For recurring or large institutional orders, request a wholesale quote.",
  },
  {
    q: "Where do you ship, and how long does it take?",
    a: "We ship within the continental United States only — no Alaska, Hawaii, Puerto Rico, US territories, or international destinations. Standard Ground is 5–7 business days ($6.99), Expedited is 2–3 business days ($14.99), and orders over $150 get free Standard Ground shipping.",
  },
  {
    q: "How are the vials packaged?",
    a: "Vials are filled, sealed, and crimped under controlled conditions, then packed in protective packaging with tamper-evident seals. All vials ship unlabeled — bare glass with no branding or markings.",
  },
  {
    q: "How should I store bacteriostatic water?",
    a: "Store sealed vials at controlled room temperature (20–25 °C), protected from light. Refer to the expiry date on the CoA for your lot.",
  },
  {
    q: "How long does bacteriostatic water last?",
    a: "Sealed vials are stable until the expiry date on the lot's CoA. Once the stopper is punctured, laboratories commonly discard the vial after 28 days, as the 0.9% benzyl alcohol preservative is intended to limit microbial growth over that period.",
  },
  {
    q: "How is bacteriostatic water different from sterile water or saline?",
    a: "Sterile water contains no preservative. Bacteriostatic water adds 0.9% benzyl alcohol to inhibit bacterial growth in multi-use vials. Saline is 0.9% sodium chloride and contains no preservative unless labelled otherwise.",
  },
  {
    q: "What is in the bacteriostatic water?",
    a: "Each lot is prepared from high-purity water and contains 0.9% benzyl alcohol as a bacteriostatic preservative. Every batch is filtered and inspected against strict internal specifications before release.",
  },
  {
    q: "Where can I find the lot certificate for my vials?",
    a: "Every lot is tested before release; certificates are available on request. Visit the Lab Results page and enter the lot number on your box or packing slip to view its Certificate of Analysis, which includes sterility, endotoxin, pH, and benzyl alcohol content.",
  },
  {
    q: "Are these products for human use?",
    a: "No. All Lab2o products are sold strictly for laboratory research purposes only. They are not for human or veterinary use or consumption of any kind, and no medical, therapeutic, diagnostic, or clinical claims are made or implied.",
  },
];

function Item({ faq, index, open, onToggle }) {
  return (
    <div className="border border-slate-200 bg-white" data-testid={`faq-item-${index}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left hover:bg-slate-50 transition-colors"
        aria-expanded={open}
        data-testid={`faq-question-${index}`}
      >
        <span className="font-chivo font-bold text-base text-slate-950">{faq.q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="px-5 sm:px-6 pb-5 text-sm text-slate-600 leading-relaxed" data-testid={`faq-answer-${index}`}>
              {faq.a}
            </p>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <PageShell
      testid="faq-page"
      overline={<span className="flex items-center gap-2"><HelpCircle className="w-3.5 h-3.5" /> Questions</span>}
      title="Frequently Asked Questions"
      intro="Ordering, shipping, storage, lot certificates, and bulk pricing — answered."
    >
      <Seo title="Bacteriostatic Water FAQ | Lab2o" path="/faq" description="Answers on bacteriostatic water composition, storage, shelf life, lot testing, CoAs, bulk orders and shipping." />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        })}
      </script>

      <div className="mt-12 max-w-3xl flex flex-col gap-3" data-testid="faq-list">
        {FAQS.map((f, i) => (
          <Item key={i} faq={f} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
        ))}
      </div>
    </PageShell>
  );
}
